import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { Link } from 'raviger';
import { Button, Card, Collapse, ListGroup, Modal, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleDown, faChevronCircleUp, faEdit, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { faHomeHeart, faPhonePlus } from '@fortawesome/pro-solid-svg-icons';
import Moment from 'react-moment';
import Header from '../components/Header';
import History from '../components/History';
import Scrollbar from '../components/Scrollbars';
import AnimalCards from '../components/AnimalCards';
import PhotoDocuments from '../components/PhotoDocuments';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import ShelterlyPrintifyButton from '../components/ShelterlyPrintifyButton';
import LoadingLink from '../components/LoadingLink';
import ActionsDropdown from '../components/ActionsDropdown';
import { useLocationWithRoutes } from "../hooks";
import Paginator from '../components/Paginator';
import { printOwnerDetails, printOwnerAnimalCareSchedules } from './Utils';

function PersonDetails({id, incident, organization}) {

  const ITEMS_PER_PAGE = 10;

  const { setShowSystemError } = useContext(SystemErrorContext);
  const { getFullLocationFromPath } = useLocationWithRoutes();
  const { state } = useContext(AuthContext);

  // Determine if this is an owner or reporter for display purposes.
  let is_owner = window.location.pathname.includes("/people/owner/")

  const [isPersonLoading, setIsPersonLoading] = useState(true);
  const [isOrgLoading, setIsOrgLoading] = useState(true);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  const [showChange, setShowChange] = useState(false);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);

  // Handle animal reunification submit.
  const handleSubmit = async () => {
    setIsPersonLoading(true);
    await axios.patch('/people/api/person/' + id + '/', {"reunite_animals":true, "incident": incident})
    .then(response => {
      setData(prevState => ({ ...prevState, "animals":prevState['animals'].map(animal => ({...animal, status:!['DECEASED', 'NO FURTHER ACTION'].includes(animal.status) ? 'REUNITED' : animal.status})) }));
      handleClose()
    })
    .catch(error => {
      setShowSystemError(true);
    })
    .finally(() => setIsPersonLoading(false));
  }

  const [data, setData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    comments: '',
    // agency: '',
    drivers_license: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    evac_zone: '',
    animals: [],
    reporter_animals: [],
    requests: [],
    images: [],
    owner_contacts: [],
    action_history: [],
    change_reasons: [],
  });

  const [organizationData, setOrganizationData] = useState({
    name: '',
    short_name: '',
    liability_name: '',
    liability_short_name: '',
  });

  function buildAnimalUrl(animal) {
    return getFullLocationFromPath(
      `/${organization}/${incident}/animals/${animal.id_for_incident}`
    );
  }

  const handleDownloadPdfClick = async () => {
    setIsPersonLoading(true);
    setIsOrgLoading(true);
    // wait for 1 tick so spinner will set before the print button locks up the browser
    await new Promise(resolve => setTimeout(resolve, 0));
    printOwnerDetails({
        owners: [data], 
        incident: state.incident.name,
        organization: organizationData,
      })
      .finally(() => {
        setIsPersonLoading(false);
        setIsOrgLoading(false);
      });
  }

  const handlePrintAllAnimalsClick = () => {
    const animals = data.animals.map((animal) => ({
      ...animal,
      url: buildAnimalUrl(animal),
      owners: [data]
    }));
    return printOwnerAnimalCareSchedules(animals, id)
  }

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();
    setIsPersonLoading(true);
    setIsOrgLoading(true);

    const fetchPersonData = async () => {
      // Fetch Person data.
      await axios.get('/people/api/person/' + id + '/?incident=' + incident, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setData(response.data);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      })
      .finally(() => setIsPersonLoading(false));
    };
    fetchPersonData();

    const fetchOrganizationData = async () => {
      // Fetch Organization data.
      await axios.get('/incident/api/organization/?slug=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setOrganizationData(response.data[0]);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      })
      .finally(() => setIsOrgLoading(false));
    };
    fetchOrganizationData();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, incident]);

  const isLoading = isPersonLoading || isOrgLoading;

  return (
    <>
    <Header>{is_owner ? 'Owner Details' : 'Reporter Details'}</Header>
    <div className="row gx-3">
      <div className="col-6 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-10px"}}>
              <h4 style={{paddingTop:"5px"}}>Information</h4>
              {isLoading ? (
                <Spinner
                  className="align-self-center me-3"
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '1.5rem',
                      width: '1.5rem',
                      marginBottom: '0.75rem'
                    }
                  }}
                />
              ) : (<>
                {is_owner ?
                <ActionsDropdown variant="secondary" title="Actions">
                      <LoadingLink
                        href={"/" + organization + "/" + incident + "/people/owner/edit/" + id}
                        isLoading={isLoading}
                        className="d-block"
                        label="Update Owner"
                      >
                        <FontAwesomeIcon icon={faEdit} className="fas" inverse />
                      </LoadingLink>
                      <ShelterlyPrintifyButton
                        id="owner-summary"
                        spinnerSize={2.0}
                        tooltipPlacement='right'
                        label='Download Owner Summary'
                        printFunc={handleDownloadPdfClick}
                        disabled={isLoading}
                        noOverlay={true}
                      />
                  </ActionsDropdown>
                  :
                  <ActionsDropdown variant="secondary" title="Actions">
                    <LoadingLink
                      href={"/" + organization + "/" + incident + "/people/reporter/edit/" + id}
                      isLoading={isLoading}
                      className="d-block"
                      label="Update Reporter"
                    >
                      <FontAwesomeIcon icon={faEdit} className="fas" inverse />
                    </LoadingLink>
                </ActionsDropdown>}
              </>)}
            </div>
            <p className="phr"/>
            <Scrollbar no_shadow="true" style={{height:"222px", marginBottom:"-10px"}} renderView={props => <div {...props} style={{...props.style, marginBottom:"-19px"}}/>} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
              <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
                <ListGroup.Item><b>Name: </b>{data.first_name} {data.last_name}</ListGroup.Item>
                {/* {data.agency ? <ListGroup.Item><b>Agency: </b>{data.agency}</ListGroup.Item>: ''} */}
                {data.phone ? <ListGroup.Item><b>Telephone: </b>{data.display_phone} {data.display_alt_phone ? <span>|&nbsp;Alt: {data.display_alt_phone}</span> : ""}</ListGroup.Item> : ""}
                {data.email ? <ListGroup.Item><b>Email: </b>{data.email}</ListGroup.Item> : ""}
                {data.address ?
                  <ListGroup.Item><b>Address: </b>{data.full_address}</ListGroup.Item>
                : ""}
                {data.requests.map(request => (
                  <ListGroup.Item key={request.id}><b>Service Request: </b><Link href={"/" + organization + "/" + incident + "/hotline/servicerequest/" + request.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>{request.full_address}</Link></ListGroup.Item>
                ))}
                {data.comments ? <ListGroup.Item><b>Comments: </b>{data.comments}</ListGroup.Item>: ''}
              </ListGroup>
            </Scrollbar>
          </Card.Body>
        </Card>
      </div>
      {is_owner ?
      <div className="col-6 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-10px"}}>
              <h4 style={{paddingTop:"5px"}}>Contact Log</h4>
              {isLoading ? (
                <Spinner
                  className="align-self-center me-3"
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '1.5rem',
                      width: '1.5rem',
                      marginBottom: '0.75rem'
                    }
                  }}
                />
              ) : (
                <ActionsDropdown variant="secondary" title="Actions">
                  <LoadingLink
                    href={"/" + organization + "/" + incident + "/hotline/ownercontact/new?owner=" + id}
                    isLoading={isLoading}
                    className="d-block"
                    label="Add Owner Contact Note"
                  >
                    <FontAwesomeIcon icon={faPhonePlus} className="fas" inverse />
                  </LoadingLink>
                </ActionsDropdown>
              )}
            </div>
            <p className="phr"/>
            <Scrollbar no_shadow="true" style={{height:"210px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
              <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
                {data.owner_contacts.map(owner_contact => (
                <ListGroup.Item key={owner_contact.id}><b><Moment format="MMMM Do YYYY HH:mm">{owner_contact.owner_contact_time}</Moment></b>
                <OverlayTrigger
                    key={"add-contact-note"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-add-contact-note`}>
                        Update owner contact note
                      </Tooltip>
                    }
                  >
                    <Link href={"/" + organization + "/" + incident + "/hotline/ownercontact/" + owner_contact.id}> <FontAwesomeIcon icon={faEdit} inverse /></Link>
                  </OverlayTrigger>
                  : {owner_contact.owner_contact_note}</ListGroup.Item>
                ))}
                {data.owner_contacts.length < 1 ? <ListGroup.Item>This owner has not been contacted yet.</ListGroup.Item> : ""}
              </ListGroup>
            </Scrollbar>
          </Card.Body>
        </Card>
      </div> : ""}
    </div>
    {data.animals.length ?
    <div className="row mt-3">
      <div className="col-12 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px", marginBottom:"-15px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-10px"}}>
              <h4 style={{paddingTop:"5px"}}>Animals</h4>
              {isLoading ? (
                <Spinner
                  className="align-self-center me-3"
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '1.5rem',
                      width: '1.5rem',
                      marginBottom: '0.75rem'
                    }
                  }}
                />
              ) : (
                <ActionsDropdown variant="secondary" title="Actions">
                  <LoadingLink
                    href={"/" + organization + "/" + incident + "/people/owner/new?owner_id=" + id}
                    isLoading={isLoading}
                    className="d-block"
                    label="Add Another Owner to All Animals"
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="fas" inverse />
                  </LoadingLink>
                  <LoadingLink
                    isLoading={isLoading}
                    className="d-block"
                    onClick={() => setShow(true)}
                    label="Reunite All Animals With Owner"
                    disabled={!data.animals.filter(animal => (!['REUNITED', 'CARE TRANSFERRED', 'DECEASED', 'NO FURTHER ACTION'].includes(animal.status))).length > 0}
                    disabled_text="No animals available to reunite."
                  >
                    <FontAwesomeIcon icon={faHomeHeart} className="fas" inverse />
                  </LoadingLink>
                  <ShelterlyPrintifyButton
                    id="owner-animal-care-schedules"
                    spinnerSize={1.5}
                    tooltipPlacement='right'
                    label='Download All Animal Care Schedules'
                    printFunc={handlePrintAllAnimalsClick}
                    disabled={data.animals.length === 0}
                  />
                </ActionsDropdown>
              )}
            </div>
            <p className="phr"/>
            <AnimalCards animals={data.animals} show_owner={false} show_status={true} organization={organization} incident={"/" + incident} />
            {data.animals.length < 1 ? <p>This owner has no animals.</p> : ""}
          </Card.Body>
        </Card>
      </div>
    </div> : ""}
    {data.reporter_animals.length ?
    <div className="row mt-3">
      <div className="col-12 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginBottom:"-17px"}}>
            <Card.Title>
              <h4 className="mb-0">Reported Animals
                <OverlayTrigger
                  key={"add-owner"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-add-owner`}>
                      Add an owner to all of these animals
                    </Tooltip>
                  }
                >
                  <Link href={"/" + organization + "/" + incident + "/people/owner/new?owner_id=" + id}><FontAwesomeIcon icon={faUserPlus} className="ms-1 fa-move-up" size="sm" inverse /></Link>
                </OverlayTrigger>
              </h4>
            </Card.Title>
            <p className="phr"/>
            <AnimalCards animals={data.reporter_animals} show_owner={false} show_status={true} organization={organization} incident={"/" + incident} />
            {data.reporter_animals.length < 1 ? <p>This reporter has no animals.</p> : ""}
          </Card.Body>
        </Card>
      </div>
    </div> : ""}
    <PhotoDocuments setData={setData} data={data} id={id} url={'/people/api/person/' + id + '/'} object={is_owner ? "Owner" : "Reporter"} isLoading={isLoading} />
    {data.change_reasons.length ? <p className="phr"/> : ""}
    {data.change_reasons.length ? <h2 className={showChange ? "" : "mb-0"}>Change Reasons<FontAwesomeIcon icon={faChevronCircleDown} hidden={showChange} onClick={() => setShowChange(!showChange)} className="ms-2" style={{verticalAlign:"middle"}} inverse /><FontAwesomeIcon icon={faChevronCircleUp} hidden={!showChange} onClick={() => setShowChange(!showChange)} className="ms-2" style={{verticalAlign:"middle"}} inverse /></h2> : ""}
    {data.change_reasons.filter(change_reason => change_reason.reason).map((change_reason, index) => (
      <Collapse key={change_reason.id} in={showChange} hidden={page !== Math.ceil((index+1)/ITEMS_PER_PAGE)}>
        <div>
          <Card className="border rounded d-flex mb-2">
            <Card.Body>
              <Moment format="MMM-DD-YYYY">{change_reason.timestamp}</Moment>. {change_reason.user_name} - {change_reason.reason}
            </Card.Body>
          </Card>
          <Paginator page={page} numPages={numPages} setPage={setPage} />
        </div>
      </Collapse>
    ))}
    <History action_history={data.action_history} />
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Animal Reunification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Have all of the animals been reunited with this owner?{data.requests.length ? <p className="mt-2">This may automatically close any related Service Requests if the animals require no further services.</p> : ""}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSubmit}>Yes</Button>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default PersonDetails;
